<template>
<div class="chapter">
<div class="inner_page">
<h2 class="calibre19" id="pgepubid00281"><a class="calibre1" id="page1"></a><span class="calibre1" id="pgepubid00282" title="p. 1"></span>I<br class="calibre11"/>
STUDENT DAYS AT EDINBURGH<br class="calibre11"/>
<span class="gutsmall">TRAVELS AND EXCURSIONS</span><br class="calibre11"/>
<span class="gutsmall">1868–1873</span></h2>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00283"><a class="calibre1" id="page3"></a><span class="calibre1" id="pgepubid00284" title="p. 3"></span>INTRODUCTION.</h3>
<p class="calibre7"><span class="smcap">The</span> following section consists
chiefly of extracts from the correspondence and journals
addressed by Louis Stevenson, as a lad of eighteen to twenty-two,
to his father and mother during summer excursions to the Scottish
coast or to the continent.  There exist enough of them to
fill a volume; but it is not in letters of this kind to his
family that a young man unbosoms himself most freely, and these
are perhaps not quite devoid of the qualities of the guide-book
and the descriptive exercise.  Nevertheless, they seem to me
to contain enough signs of the future master-writer, enough of
character, observation, and skill in expression, to make a few
worth giving by way of an opening chapter to the present
book.  Among them are interspersed one or two of a different
character addressed to other correspondents.</p>
<p class="calibre7">But, first, it is desirable that readers not acquainted with
the circumstances and conditions of Stevenson’s parentage
and early life should be here, as briefly as possible, informed
of them.  On both sides of the house he came of capable and
cultivated stock.  His grandfather was Robert Stevenson,
civil engineer, highly distinguished as the builder of the Bell
Rock lighthouse.  By this Robert Stevenson, his three sons,
and two of his grandsons now living, the business of civil
engineers in general, and of official engineers to the
Commissioners <a id="page4"></a><span id="pgepubid00285" title="p. 4"></span>of Northern Lights in particular, has been carried on at
Edinburgh with high credit and public utility for almost a
century.  Thomas Stevenson, the youngest of the three sons
of the original Robert, was Robert Louis Stevenson’s
father.  He was a man not only of mark, zeal, and
inventiveness in his profession, but of a singularly interesting
personality; a staunch friend and sagacious adviser, trenchant in
judgment and demonstrative in emotion, outspoken,
dogmatic,—despotic, even, in little things, but withal
essentially chivalrous and soft-hearted; apt to pass with the
swiftest transition from moods of gloom or sternness to those of
tender or freakish gaiety, and commanding a gift of humorous and
figurative speech second only to that of his more famous son.</p>
<p class="calibre7">Thomas Stevenson was married to Margaret Isabella, youngest
daughter of the Rev. Lewis Balfour, for many years minister of
the parish of Colinton in Midlothian.  This Mr. Balfour
(described by his grandson in the essay called ‘The
Manse’) was of the stock of the Balfours of Pilrig, and
grandson to that James Balfour, professor first of moral
philosophy, and afterwards of the law of nature and of nations,
who was held in particular esteem as a philosophical
controversialist by David Hume.  His wife, Henrietta Smith,
a daughter of the Rev. George Smith of Galston, to whose gift as
a preacher Burns refers scoffingly in the <i class="calibre12">Holy Fair</i>, is
said to have been a woman of uncommon beauty and charm of
manner.  Their daughter, Mrs. Thomas Stevenson, suffered in
early and middle life from chest and nerve troubles, and her son
may have inherited from her some of his constitutional <a id="page5"></a><span id="pgepubid00286" title="p. 5"></span>weakness as
well as of his social and intellectual vivacity and his taste for
letters.  Robert Louis (baptized Robert Lewis Balfour)
Stevenson was born on November 13, 1850, at 8 Howard Place,
Edinburgh, and was the only child of his parents.  His
health was infirm from the first, and he was with difficulty kept
alive by the combined care of a capable and watchful mother and a
perfectly devoted nurse, Alison Cunningham; to whom his lifelong
gratitude will be found touchingly expressed in the course of the
following letters.  In 1858 he was near dying of a gastric
fever, and was at all times subject to acute catarrhal and
bronchial affections and extreme nervous excitability.  In
January 1853 his parents moved to 1 Inverleith Terrace, and in
May 1857 to 17 Heriot Row, which continued to be their Edinburgh
home until the death of Thomas Stevenson in 1887.  Much of
his time was also spent in the manse of Colinton on the Water of
Leith, the home of his maternal grandfather.  Of this place
his childish recollections were happy and idyllic, while those of
city life were coloured rather by impressions of sickness, fever,
and nocturnal terrors.  If, however, he suffered much as a
child from the distresses, he also enjoyed to the full the
pleasures, of imagination.  Illness confined him much within
the house, but imagination kept him always content and
busy.  In the days of the Crimean war some one gave the
child a cheap toy sword; and when his father depreciated it, he
said, ‘I tell you, the sword is of gold, and the sheath of
silver, and the boy is very well off and quite
contented.’  As disabilities closed in on him in after
<a id="page6"></a><span id="pgepubid00287" title="p. 6"></span>life, he
would never grumble at any gift, however niggardly, of fortune,
and the anecdote is as characteristic of the man as of the
child.  He was eager and full of invention in every kind of
play, whether solitary or sociable, and seems to have been
treated as something of a small, sickly prince among a whole
cousinhood of playmates of both the Balfour and the Stevenson
connections.  He was also a greedy reader, or rather
listener to reading; for it was not until his eighth year that he
began to read easily or habitually to himself.  He has
recorded how his first conscious impression of pleasure from the
sound and cadence of words was received from certain passages in
M‘Cheyne’s hymns as recited to him by his
nurse.  Bible stories, the <i class="calibre12">Pilgrim’s Progress</i>,
and Mayne Reid’s tales were especially, and it would seem
equally, his delight.  He began early to take pleasure in
attempts at composition of his own.  A history of Moses,
dictated in his sixth year, and an account of travels in Perth,
in his ninth, are still extant.  Ill health prevented him
getting much regular or continuous schooling.  He attended
first (1858–61) a preparatory school kept by a Mr.
Henderson in India Street; and next (at intervals for some time
after the autumn of 1861) the Edinburgh Academy.  One of his
tutors at the former school writes: ‘He was the most
delightful boy I ever knew; full of fun, full of tender feeling,
ready for his lessons, ready for a story, ready for
fun.’  From very early days, both as child and boy, he
must have had something of that power to charm which
distinguished him above other men in after life.  ‘I
loike that bo-o-o-o-y,’ a heavy Dutchman <a id="page7"></a><span id="pgepubid00288" title="p. 7"></span>was heard
saying to himself over and over again, whom at the age of about
thirteen he had held in amused conversation during a whole
passage from Ostend.  The same quality, with the signs which
he always showed of quick natural intelligence when he chose to
learn, must have helped to spare him many punishments from
teachers which he earned by persistent and ingenious
truantry.  ‘I think,’ remarks his mother,
‘they liked talking to him better than teaching
him.’</p>
<p class="calibre7">For a few months in the autumn of 1863, when his parents had
been ordered to winter at Mentone for the sake of his
mother’s health, he was sent to a boarding-school kept by a
Mr. Wyatt at Spring Grove, near London.  It is not my
intention to treat the reader to the series of childish and
boyish letters of these days which parental fondness has
preserved.  But here is one written from his English school
when he was about thirteen, which is both amusing in itself and
had a certain influence on his destiny, inasmuch as his appeal
led to his being taken out to join his parents on the French
Riviera; which from that day forward he never ceased to love, and
for which the longing, amid the gloom of Edinburgh winters, often
afterwards gripped him by the heart.</p>
<p class="calibre22"><i class="calibre12">Spring Grove School</i>,
12<i class="calibre12">th</i> <i class="calibre12">November</i> 1863.</p>
<p class="calibre7"><span class="gutsmall1">MA CHERE MAMAN</span>,—Jai recu
votre lettre Aujourdhui et comme le jour prochaine est mon jour
de naisance je vous écrit ce lettre.  Ma grande
gatteaux est arrivé il leve 12 livres et demi le prix
etait 17 shillings.  Sur la soirée de Monseigneur
Faux il y etait quelques belles feux d’artifice.  Mais
les polissons entrent dans notre champ et nos feux <a id="page8"></a><span id="pgepubid00289" title="p. 8"></span>d’artifice et handkerchiefs disappeared quickly,
but we charged them out of the field.  Je suis presque
driven mad par une bruit terrible tous les garcons kik up comme
grand un bruit qu’ll est possible.  I hope you will
find your house at Mentone nice.  I have been obliged to
stop from writing by the want of a pen, but now I have one, so I
will continue.</p>
<p class="calibre7">My dear papa, you told me to tell you whenever I was
miserable.  I do not feel well, and I wish to get home.</p>
<p class="calibre7">Do take me with you.</p>
<p class="calibre22">R. <span class="smcap">Stevenson</span>.</p>
<p class="calibre22">2 <i class="calibre12">Sulyarde Terrace</i>,
<i class="calibre12">Torquay</i>, <i class="calibre12">Thursday</i> (<i class="calibre12">April</i> 1866).</p>
<p class="calibre7"><span class="gutsmall1">RESPECTED PATERNAL
RELATIVE</span>,—I write to make a request of the most
moderate nature.  Every year I have cost you an
enormous—nay, elephantine—sum of money for drugs and
physician’s fees, and the most expensive time of the twelve
months was March.</p>
<p class="calibre7">But this year the biting Oriental blasts, the howling
tempests, and the general ailments of the human race have been
successfully braved by yours truly.</p>
<p class="calibre7">Does not this deserve remuneration?</p>
<p class="calibre7">I appeal to your charity, I appeal to your generosity, I <a id="page9"></a><span id="pgepubid00290" title="p. 9"></span>appeal to your
justice, I appeal to your accounts, I appeal, in fine, to your
purse.</p>
<p class="calibre7">My sense of generosity forbids the receipt of more—my
sense of justice forbids the receipt of less—than
half-a-crown.—Greeting from, Sir, your most affectionate
and needy son,</p>
<p class="calibre22">R. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00291"><a class="calibre1" id="page15"></a><span class="calibre1" id="pgepubid00292" title="p. 15"></span><span class="smcap1">to Mrs. Thomas Stevenson</span></h3>
<p class="calibre22"><i class="calibre12">Wick</i>, <i class="calibre12">Friday</i>,
<i class="calibre12">September</i> 11, 1868.</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR MOTHER</span>,—. . . Wick
lies at the end or elbow of an open triangular bay, hemmed on
either side by shores, either cliff or steep earth-bank, of no
great height.  The grey houses of Pulteney extend along the
southerly shore almost to the cape; and it is about half-way down
this shore—no, six-sevenths way down—that the new
breakwater extends athwart the bay.</p>
<p class="calibre7">Certainly Wick in itself possesses no beauty: bare, grey
shores, grim grey houses, grim grey sea; not even the gleam of
red tiles; not even the greenness of a tree.  The southerly
heights, when I came here, were black with people, fishers
waiting on wind and night.  Now all the S.Y.S. (Stornoway
boats) have beaten out of the bay, and the Wick men stay indoors
or wrangle on the quays <a id="page16"></a><span id="pgepubid00293" title="p. 16"></span>with dissatisfied fish-curers,
knee-high in brine, mud, and herring refuse.  The day when
the boats put out to go home to the Hebrides, the girl here told
me there was ‘a black wind’; and on going out, I
found the epithet as justifiable as it was picturesque.  A
cold, <i class="calibre12">black</i> southerly wind, with occasional rising showers
of rain; it was a fine sight to see the boats beat out a-teeth of
it.</p>
<p class="calibre7">In Wick I have never heard any one greet his neighbour with
the usual ‘Fine day’ or ‘Good
morning.’  Both come shaking their heads, and both
say, ‘Breezy, breezy!’  And such is the
atrocious quality of the climate, that the remark is almost
invariably justified by the fact.</p>
<p class="calibre7">The streets are full of the Highland fishers, lubberly,
stupid, inconceivably lazy and heavy to move.  You bruise
against them, tumble over them, elbow them against the
wall—all to no purpose; they will not budge; and you are
forced to leave the pavement every step.</p>
<p class="calibre7">To the south, however, is as fine a piece of coast scenery as
I ever saw.  Great black chasms, huge black cliffs, rugged
and over-hung gullies, natural arches, and deep green pools below
them, almost too deep to let you see the gleam of sand among the
darker weed: there are deep caves too.  In one of these
lives a tribe of gipsies.  The men are <i class="calibre12">always</i> drunk,
simply and truthfully always.  From morning to evening the
great villainous-looking fellows are either sleeping off the last
debauch, or hulking about the cove ‘in the
horrors.’  The cave is deep, high, and airy, and might
be made comfortable enough.  But they just live among heaped
boulders, damp with continual droppings from above, with no more
furniture than two or three tin pans, a truss of rotten straw,
and a few ragged cloaks.  In winter the surf bursts into the
mouth and often forces them to abandon it.</p>
<p class="calibre7">An <i class="calibre12">émeute</i> of disappointed fishers was feared,
and two ships of war are in the bay to render assistance to the
municipal authorities.  This is the ides; and, to all
intents and purposes, said ides are passed.  Still there is
a good <a id="page17"></a><span id="pgepubid00294" title="p. 17"></span>deal of disturbance, many drunk men, and a double supply
of police.  I saw them sent for by some people and enter an
inn, in a pretty good hurry: what it was for I do not know.</p>
<p class="calibre7">You would see by papa’s letter about the carpenter who
fell off the staging: I don’t think I was ever so much
excited in my life.  The man was back at his work, and I
asked him how he was; but he was a Highlander, and—need I
add it?—dickens a word could I understand of his
answer.  What is still worse, I find the people
here-about—that is to say, the Highlanders, not the
northmen—don’t understand <i class="calibre12">me</i>.</p>
<p class="calibre7">I have lost a shilling’s worth of postage stamps, which
has damped my ardour for buying big lots of ’em: I’ll
buy them one at a time as I want ’em for the future.</p>
<p class="calibre7">The Free Church minister and I got quite thick.  He left
last night about two in the morning, when I went to turn
in.  He gave me the enclosed.—I remain your
affectionate son,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00295"><span class="smcap1">to Mrs. Thomas Stevenson</span></h3>
<p class="calibre22"><i class="calibre12">Wick</i>, September 5,
1868.  <i class="calibre12">Monday</i>.</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR MAMMA</span>,—This
morning I got a delightful haul: your letter of the fourth
(surely mis-dated); Papa’s of same day; Virgil’s
<i class="calibre12">Bucolics</i>, very thankfully received; and Aikman’s
<i class="calibre12">Annals</i>, <a id="citation17"></a><span class="citation" href="#/Text/Section0011#footnote17" title="Link target outside of Sample">[17]</span> a precious and most acceptable
donation, for which I tender my most ebullient
thanksgivings.  I almost forgot to drink my tea and eat mine
egg.</p>
<p class="calibre7">It contains more detailed accounts than anything I ever saw,
except Wodrow, without being so portentously tiresome and so
desperately overborne with footnotes, proclamations, acts of
Parliament, and citations as that last history.</p>
<p class="calibre7"><a id="page18"></a><span id="pgepubid00296" title="p. 18"></span>I have
been reading a good deal of Herbert.  He’s a clever
and a devout cove; but in places awfully twaddley (if I may use
the word).  Oughtn’t this to rejoice Papa’s
heart—</p>
<p class="poetry">‘Carve or discourse; do not a famine
fear.<br class="calibre5"/>
Who carves is kind to two, who talks to all.’</p>
<p class="calibre7">You understand?  The ‘fearing a famine’ is
applied to people gulping down solid vivers without a word, as if
the ten lean kine began to-morrow.</p>
<p class="calibre7">Do you remember condemning something of mine for being too
obtrusively didactic.  Listen to Herbert—</p>
<p class="poetry">‘Is it not verse except enchanted
groves<br class="calibre5"/>
And sudden arbours shadow coarse-spun lines?<br class="calibre5"/>
Must purling streams refresh a lover’s loves?<br class="calibre5"/>
<i class="calibre12">Must all be veiled</i>, <i class="calibre12">while he that reads divines</i><br class="calibre5"/>
<i class="calibre12">Catching the sense at two removes</i>?’</p>
<p class="calibre7">You see, ‘except’ was used for
‘unless’ before 1630.</p>
<div class="gapspace"> </div>
<p class="calibre7"><i class="calibre12">Tuesday</i>.—The riots were a hum.  No more has
been heard; and one of the war-steamers has deserted in
disgust.</p>
<p class="calibre7">The <i class="calibre12">Moonstone</i> is frightfully interesting: isn’t
the detective prime?  Don’t say anything about the
plot; for I have only read on to the end of Betteredge’s
narrative, so don’t know anything about it yet.</p>
<p class="calibre7">I thought to have gone on to Thurso to-night, but the coach
was full; so I go to-morrow instead.</p>
<p class="calibre7">To-day I had a grouse: great glorification.</p>
<p class="calibre7">There is a drunken brute in the house who disturbed my rest
last night.  He’s a very respectable man in general,
but when on the ‘spree’ a most consummate fool. 
When he came in he stood on the top of the stairs and preached in
the dark with great solemnity and no audience from 12 <span class="gutsmall1">P.M.</span> to half-past one.  At last I
opened my door.  ‘Are we to have no sleep at all for
that <i class="calibre12">drunken brute</i>?’  I said.  As I hoped,
it had the desired effect.  <a id="page19"></a><span id="pgepubid00297" title="p. 19"></span>‘Drunken brute!’ he
howled, in much indignation; then after a pause, in a voice of
some contrition, ‘Well, if I am a drunken brute, it’s
only once in the twelvemonth!’  And that was the end
of him; the insult rankled in his mind; and he retired to
rest.  He is a fish-curer, a man over fifty, and pretty rich
too.  He’s as bad again to-day; but I’ll be shot
if he keeps me awake, I’ll douse him with water if he makes
a row.—Ever your affectionate son,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00298"><span class="smcap1">to Mrs. Thomas Stevenson</span></h3>
<p class="calibre22"><i class="calibre12">Wick</i>, <i class="calibre12">September</i>
1868.  <i class="calibre12">Saturday</i>, 10 <span class="gutsmall1">A.M.</span></p>
<p class="calibre7"><span class="gutsmall1">MY DEAR MOTHER</span>,—The last
two days have been dreadfully hard, and I was so tired in the
evenings that I could not write.  In fact, last night I went
to sleep immediately after dinner, or very nearly so.  My
hours have been 10–2 and 3–7 out in the lighter or
the small boat, in a long, heavy roll from the
nor’-east.  When the dog was taken out, he got awfully
ill; one of the men, Geordie Grant by name and surname, followed
<i class="calibre12">shoot</i> with considerable <i class="calibre12">éclat</i>; but,
wonderful to relate! I kept well.  My hands are all skinned,
blistered, discoloured, and engrained with tar, some of which
latter has established itself under my nails in a position of
such natural strength that it defies all my efforts to dislodge
it.  The worst work I had was when David (MacDonald’s
eldest) and I took the charge ourselves.  He remained in the
lighter to tighten or slacken the guys as we raised the pole
towards the perpendicular, with two men.  I was with four
men in the boat.  We dropped an anchor out a good bit, then
tied a cord to the pole, took a turn round the sternmost thwart
with it, and pulled on the anchor line.  As the great, big,
wet hawser came in it soaked you to the skin: I was the sternest
(used, by way of variety, for sternmost) of the lot, and had to
coil it—a work which involved, from <i class="calibre12">its</i> being so
stiff and <i class="calibre12">your</i> <a id="page20"></a><span id="pgepubid00299" title="p. 20"></span>being busy pulling with all your
might, no little trouble and an extra ducking.  We got it
up; and, just as we were going to sing ‘Victory!’ one
of the guys slipped in, the pole tottered—went over on its
side again like a shot, and behold the end of our labour.</p>
<p class="calibre7">You see, I have been roughing it; and though some parts of the
letter may be neither very comprehensible nor very interesting to
<i class="calibre12">you</i>, I think that perhaps it might amuse Willie Traquair,
who delights in all such dirty jobs.</p>
<p class="calibre7">The first day, I forgot to mention, was like mid-winter for
cold, and rained incessantly so hard that the livid white of our
cold-pinched faces wore a sort of inflamed rash on the windward
side.</p>
<p class="calibre7">I am not a bit the worse of it, except fore-mentioned state of
hands, a slight crick in my neck from the rain running down, and
general stiffness from pulling, hauling, and tugging for dear
life.</p>
<p class="calibre7">We have got double weights at the guys, and hope to get it up
like a shot.</p>
<p class="calibre7">What fun you three must be having!  I hope the cold
don’t disagree with you.—I remain, my dear mother,
your affectionate son,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00300"><span class="smcap1">to Mrs. Thomas Stevenson</span></h3>
<p class="calibre22"><i class="calibre12">Pulteney</i>, <i class="calibre12">Wick</i>,
<i class="calibre12">Sunday</i>, <i class="calibre12">September</i> 1868.</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR MOTHER</span>,—Another
storm: wind higher, rain thicker: the wind still rising as the
night closes in and the sea slowly rising along with it; it looks
like a three days’ gale.</p>
<p class="calibre7"><a id="page21"></a><span id="pgepubid00301" title="p. 21"></span>Last
week has been a blank one: always too much sea.</p>
<p class="calibre7">I enjoyed myself very much last night at the R.’s. 
There was a little dancing, much singing and supper.</p>
<p class="calibre7">Are you not well that you do not write?  I haven’t
heard from you for more than a fortnight.</p>
<p class="calibre7">The wind fell yesterday and rose again to-day; it is a
dreadful evening; but the wind is keeping the sea down as
yet.  Of course, nothing more has been done to the poles;
and I can’t tell when I shall be able to leave, not for a
fortnight yet, I fear, at the earliest, for the winds are
persistent.  Where’s Murra?  Is Cummie struck
dumb about the boots?  I wish you would get somebody to
write an interesting letter and say how you are, for you’re
on the broad of your back I see.  There hath arrived an
inroad of farmers to-night; and I go to avoid them to M— if
he’s disengaged, to the R.’s if not.</p>
<div class="gapspace"> </div>
<p class="calibre7"><i class="calibre12">Sunday</i> (<i class="calibre12">later</i>).—Storm without: wind and
rain: a confused mass of wind-driven rain-squalls, wind-ragged
mist, foam, spray, and great, grey waves.  Of this
hereafter; in the meantime let us follow the due course of
historic narrative.</p>
<p class="calibre7">Seven <span class="gutsmall1">P.M.</span> found me at
Breadalbane Terrace, clad in spotless blacks, white tie, shirt,
et cætera, and finished off below with a pair of
navvies’ boots.  How true that the devil is betrayed
by his feet!  A message to Cummy at last.  Why, O
treacherous woman! were my dress boots withheld?</p>
<p class="calibre7">Dramatis personæ: père R., amusing, long-winded,
in many points like papa; mère R., nice, delicate, likes
hymns, knew Aunt Margaret (’t’ould man knew Uncle
Alan); fille R., nommée Sara (no h), rather nice, lights
up well, good voice, <i class="calibre12">interested</i> face; Miss L., nice also,
washed out a little, and, I think, a trifle sentimental; fils R.,
in a Leith office, smart, full of happy epithet, amusing. 
They are very nice and very kind, asked me to come
back—‘any night you feel dull; and any night <a id="page22"></a><span id="pgepubid00302" title="p. 22"></span>doesn’t
mean no night: we’ll be so glad to see you.’ 
<i class="calibre12">Cest la mère qui parle</i>.</p>
<p class="calibre7">I was back there again to-night.  There was hymn-singing,
and general religious controversy till eight, after which talk
was secular.  Mrs. S. was deeply distressed about the boot
business.  She consoled me by saying that many would be glad
to have such feet whatever shoes they had on. 
Unfortunately, fishers and seafaring men are too facile to be
compared with!  This looks like enjoyment: better speck than
Anster.</p>
<p class="calibre7">I have done with frivolity.  This morning I was awakened
by Mrs. S. at the door.  ‘There’s a ship ashore
at Shaltigoe!’  As my senses slowly flooded, I heard
the whistling and the roaring of wind, and the lashing of
gust-blown and uncertain flaws of rain.  I got up, dressed,
and went out.  The mizzled sky and rain blinded you.</p>
<p class="calibre8">
<span href="#/Text/Section0013" id="id-1668472753755671724" title="Link target outside of Sample">
<img alt="Diagram" class="calibre9" id="id-1529461533761088223" src="Images/img0004.jpg" title="Diagram"/>
</span></p>
<p class="calibre7">C D is the new pier.</p>
<p class="calibre7">A the schooner ashore.  B the salmon house.</p>
<p class="calibre7">She was a Norwegian: coming in she saw our first gauge-pole,
standing at point E. Norse skipper thought it was a sunk smack,
and dropped his anchor in full drift of sea: chain broke:
schooner came ashore.  Insured laden with wood: skipper
owner of vessel and cargo bottom out.</p>
<p class="calibre7">I was in a great fright at first lest we should be liable; but
it seems that’s all right.</p>
<p class="calibre7">Some of the waves were twenty feet high.  The spray <a id="page23"></a><span id="pgepubid00303" title="p. 23"></span>rose eighty
feet at the new pier.  Some wood has come ashore, and the
roadway seems carried away.  There is something fishy at the
far end where the cross wall is building; but till we are able to
get along, all speculation is vain.</p>
<p class="calibre7">I am so sleepy I am writing nonsense.</p>
<p class="calibre7">I stood a long while on the cope watching the sea below me; I
hear its dull, monotonous roar at this moment below the shrieking
of the wind; and there came ever recurring to my mind the verse I
am so fond of:—</p>
<p class="poetry">‘But yet the Lord that is on high<br class="calibre5"/>
   Is more of might by far<br class="calibre5"/>
Than noise of many waters is<br class="calibre5"/>
   Or great sea-billows are.’</p>
<p class="calibre7">The thunder at the wall when it first struck—the rush
along ever growing higher—the great jet of snow-white spray
some forty feet above you—and the ‘noise of many
waters,’ the roar, the hiss, the ‘shrieking’
among the shingle as it fell head over heels at your feet. 
I watched if it threw the big stones at the wall; but it never
moved them.</p>
<div class="gapspace"> </div>
<p class="calibre7"><i class="calibre12">Monday</i>.—The end of the work displays gaps, cairns
of ten ton blocks, stones torn from their places and turned right
round.  The damage above water is comparatively little: what
there may be below, <i class="calibre12">on ne sait pas encore</i>.  The
roadway is torn away, cross heads, broken planks tossed here and
there, planks gnawn and mumbled as if a starved bear had been
trying to eat them, planks with spales lifted from them as if
they had been dressed with a rugged plane, one pile swaying to
and fro clear of the bottom, the rails in one place sunk a foot
at least.  This was not a great storm, the waves were light
and short.  Yet when we are standing at the office, I felt
the ground beneath me <i class="calibre12">quail</i> as a huge roller thundered on
the work at the last year’s cross wall.</p>
<p class="calibre7"><a id="page24"></a><span id="pgepubid00304" title="p. 24"></span>How
could <i class="calibre12">noster amicus Q. maximus</i> appreciate a storm at
Wick?  It requires a little of the artistic temperament, of
which Mr. T. S., <a id="citation24"></a><span class="citation" href="#/Text/Section0011#footnote24" title="Link target outside of Sample">[24]</span> C.E., possesses some, whatever he may
say.  I can’t look at it practically however: that
will come, I suppose, like grey hair or coffin nails.</p>
<p class="calibre7">Our pole is snapped: a fortnight’s work and the loss of
the Norse schooner all for nothing!—except experience and
dirty clothes.—Your affectionate son,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00305"><span class="smcap1">to Mrs. Churchill babington</span></h3>
<p class="calibre22">[<i class="calibre12">Swanston Cottage</i>,
<i class="calibre12">Lothianburn</i>, <i class="calibre12">Summer</i> 1871.]</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR MAUD</span>,—If you have
forgotten the hand-writing—as is like enough—you will
find the name of a former correspondent (don’t know how to
spell that word) at the end.  I have begun to write to you
before now, but always stuck somehow, and left it to drown in a
drawerful of like fiascos.  This time I am determined to
carry through, though I have nothing specially to say.</p>
<p class="calibre7">We look fairly like summer this morning; the trees are
blackening out of their spring greens; the warmer suns have
melted the hoarfrost of daisies of the paddock; and the
blackbird, I fear, already beginning to ‘stint his pipe of
mellower days’—which is very apposite (I can’t
spell anything to-day—<i class="calibre12">one</i> p or <i class="calibre12">two</i>?) and
pretty.  All the same, we have been having shocking
weather—cold winds and grey skies.</p>
<p class="calibre7">I have been reading heaps of nice books; but I can’t go
<a id="page25"></a><span id="pgepubid00306" title="p. 25"></span>back so
far.  I am reading Clarendon’s <i class="calibre12">Hist. Rebell.</i> at
present, with which I am more pleased than I expected, which is
saying a good deal.  It is a pet idea of mine that one gets
more real truth out of one avowed partisan than out of a dozen of
your sham impartialists—wolves in sheep’s
clothing—simpering honesty as they suppress
documents.  After all, what one wants to know is not what
people did, but why they did it—or rather, why they
<i class="calibre12">thought</i> they did it; and to learn that, you should go to
the men themselves.  Their very falsehood is often more than
another man’s truth.</p>
<p class="calibre7">I have possessed myself of Mrs. Hutchinson, which, of course,
I admire, etc.  But is there not an irritating deliberation
and correctness about her and everybody connected with her? 
If she would only write bad grammar, or forget to finish a
sentence, or do something or other that looks fallible, it would
be a relief.  I sometimes wish the old Colonel had got drunk
and beaten her, in the bitterness of my spirit.  I know I
felt a weight taken off my heart when I heard he was
extravagant.  It is quite possible to be too good for this
evil world; and unquestionably, Mrs. Hutchinson was.  The
way in which she talks of herself makes one’s blood run
cold.  There—I am glad to have got that out—but
don’t say it to anybody—seal of secrecy.</p>
<p class="calibre7">Please tell Mr. Babington that I have never forgotten one of
his drawings—a Rubens, I think—a woman holding up a
model ship.  That woman had more life in her than ninety per
cent. of the lame humans that you see crippling about this
earth.</p>
<p class="calibre7">By the way, that is a feature in art which seems to have come
in with the Italians.  Your old Greek statues have scarce
enough vitality in them to keep their monstrous bodies fresh
withal.  A shrewd country attorney, in a turned white
neckcloth and rusty blacks, would just take one of these
Agamemnons and Ajaxes quietly by his beautiful, strong arm, trot
the unresisting statue down a <a id="page26"></a><span id="pgepubid00307" title="p. 26"></span>little gallery of legal shams, and
turn the poor fellow out at the other end, ‘naked, as from
the earth he came.’  There is more latent life, more
of the coiled spring in the sleeping dog, about a recumbent
figure of Michael Angelo’s than about the most excited of
Greek statues.  The very marble seems to wrinkle with a wild
energy that we never feel except in dreams.</p>
<p class="calibre7">I think this letter has turned into a sermon, but I had
nothing interesting to talk about.</p>
<p class="calibre7">I do wish you and Mr. Babington would think better of it and
come north this summer.  We should be so glad to see you
both.  <i class="calibre12">Do</i> reconsider it.—Believe me, my dear
Maud, ever your most affectionate cousin,</p>
<p class="calibre22"><span class="smcap">Louis
Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00308"><span class="smcap1">to Alison Cunningham</span></h3>
<p class="calibre22">1871?</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR CUMMY</span>,—I was
greatly pleased by your letter in many ways.  Of course, I
was glad to hear from you; you know, you and I have so many old
stories between us, that even if there was nothing else, even if
there was not a very sincere respect and affection, we should
always be glad to pass a nod.  I say ‘even if there
was not.’  But you know right well there is.  Do
not suppose that I shall ever forget those long, bitter nights,
when I coughed and coughed and was so unhappy, and you were so
patient and loving with a poor, sick child.  Indeed, Cummy,
I wish I might become a man worth talking of, if it were only
that you should not have thrown away your pains.</p>
<p class="calibre7"><a id="page27"></a><span id="pgepubid00309" title="p. 27"></span>Happily, it is not the result of our acts that makes
them brave and noble, but the acts themselves and the unselfish
love that moved us to do them.  ‘Inasmuch as you have
done it unto one of the least of these.’  My dear old
nurse, and you know there is nothing a man can say nearer his
heart except his mother or his wife—my dear old nurse, God
will make good to you all the good that you have done, and
mercifully forgive you all the evil.  And next time when the
spring comes round, and everything is beginning once again, if
you should happen to think that you might have had a child of
your own, and that it was hard you should have spent so many
years taking care of some one else’s prodigal, just you
think this—you have been for a great deal in my life; you
have made much that there is in me, just as surely as if you had
conceived me; and there are sons who are more ungrateful to their
own mothers than I am to you.  For I am not ungrateful, my
dear Cummy, and it is with a very sincere emotion that I write
myself your little boy,</p>
<p class="calibre22"><span class="smcap">Louis</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00310"><span class="smcap1">to Charles Baxter</span></h3>
<p class="calibre22"><i class="calibre12">Dunblane</i>, <i class="calibre12">Friday</i>,
5<i class="calibre12">th</i> <i class="calibre12">March</i> 1872.</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR BAXTER</span>,—By the
date you may perhaps understand the purport of my letter without
any words wasted about the matter.  I cannot walk with you
to-morrow, and you must not expect me.  I came yesterday
afternoon to Bridge of Allan, and have been very happy ever
since, as every place is sanctified by the eighth sense,
Memory.  I walked up here this morning (three miles,
<i class="calibre12">tu-dieu</i>! a good stretch for me), and passed one of my
favourite places in the world, and one that I very much affect in
spirit when the body is tied down and brought immovably <a id="page28"></a><span id="pgepubid00311" title="p. 28"></span>to anchor on
a sickbed.  It is a meadow and bank on a corner on the
river, and is connected in my mind inseparably with
Virgil’s <i class="calibre12">Eclogues</i>.  <i class="calibre12">Hic corulis mistos inter
consedimus ulmos</i>, or something very like that, the passage
begins (only I know my short-winded Latinity must have come to
grief over even this much of quotation); and here, to a wish, is
just such a cavern as Menalcas might shelter himself withal from
the bright noon, and, with his lips curled backward, pipe himself
blue in the face, while <i class="calibre12">Messieurs les Arcadiens</i> would roll
out those cloying hexameters that sing themselves in one’s
mouth to such a curious lifting chant.</p>
<p class="calibre7">In such weather one has the bird’s need to whistle; and
I, who am specially incompetent in this art, must content myself
by chattering away to you on this bit of paper.  All the way
along I was thanking God that he had made me and the birds and
everything just as they are and not otherwise; for although there
was no sun, the air was so thrilled with robins and blackbirds
that it made the heart tremble with joy, and the leaves are far
enough forward on the underwood to give a fine promise for the
future.  Even myself, as I say, I would not have had changed
in one <i class="calibre12">iota</i> this forenoon, in spite of all my idleness and
Guthrie’s lost paper, which is ever present with me—a
horrible phantom.</p>
<p class="calibre7">No one can be alone at home or in a quite new place. 
Memory and you must go hand in hand with (at least) decent
weather if you wish to cook up a proper dish of solitude. 
It is in these little flights of mine that I get more pleasure
than in anything else.  Now, at present, I am supremely
uneasy and restless—almost to the extent of pain; but O!
how I enjoy it, and how I <i class="calibre12">shall</i> enjoy it afterwards
(please God), if I get years enough allotted to me for the thing
to ripen in.  When I am a very old and very respectable
citizen with white hair and bland manners and a gold watch, I
shall hear three crows cawing in my heart, as I heard them this
morning: I vote for <a id="page29"></a><span id="pgepubid00312" title="p. 29"></span>old age and eighty years of retrospect.  Yet, after
all, I dare say, a short shrift and a nice green grave are about
as desirable.</p>
<p class="calibre7">Poor devil! how I am wearying you!  Cheer up.  Two
pages more, and my letter reaches its term, for I have no more
paper.  What delightful things inns and waiters and bagmen
are!  If we didn’t travel now and then, we should
forget what the feeling of life is.  The very cushion of a
railway carriage—‘the things restorative to the
touch.’  I can’t write, confound it! 
That’s because I am so tired with my walk.  Believe
me, ever your affectionate friend,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00313"><span class="smcap1">to Charles Baxter</span></h3>
<p class="calibre22"><i class="calibre12">Dunblane</i>, <i class="calibre12">Tuesday</i>,
9<i class="calibre12">th</i> <i class="calibre12">April</i> 1872.</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR BAXTER</span>,—I
don’t know what you mean.  I know nothing about the
Standing Committee of the Spec., did not know that such a body
existed, and even if it doth exist, must sadly repudiate all
association with such ‘goodly fellowship.’  I am
a ‘Rural Voluptuary’ at present.  <i class="calibre12">That</i> is
what is the matter with me.  The Spec. may go whistle. 
As for ‘C. Baxter, Esq.,’ who is he?  ‘One
Baxter, or Bagster, a secretary,’ I say to mine
acquaintance, ‘is at present disquieting my leisure with
certain illegal, uncharitable, unchristian, and unconstitutional
documents called <i class="calibre12">Business Letters</i>: <i class="calibre12">The affair is in the
hands of the Police</i>.’  Do you hear <i class="calibre12">that</i>,
you evildoer?  Sending business letters is surely a far more
hateful and slimy degree of wickedness than sending threatening
letters; the man who throws grenades and torpedoes is less
malicious; the Devil in red-hot hell rubs his hands with glee as
he reckons <a id="page30"></a><span id="pgepubid00314" title="p. 30"></span>up the number that go forth spreading pain and anxiety
with each delivery of the post.</p>
<p class="calibre7">I have been walking to-day by a colonnade of beeches along the
brawling Allan.  My character for sanity is quite gone,
seeing that I cheered my lonely way with the following, in a
triumphant chaunt: ‘Thank God for the grass, and the
fir-trees, and the crows, and the sheep, and the sunshine, and
the shadows of the fir-trees.’  I hold that he is a
poor mean devil who can walk alone, in such a place and in such
weather, and doesn’t set up his lungs and cry back to the
birds and the river.  Follow, follow, follow me.  Come
hither, come hither, come hither—here shall you
see—no enemy—except a very slight remnant of winter
and its rough weather.  My bedroom, when I awoke this
morning, was full of bird-songs, which is the greatest pleasure
in life.  Come hither, come hither, come hither, and when
you come bring the third part of the <i class="calibre12">Earthly Paradise</i>; you
can get it for me in Elliot’s for two and tenpence (2s.
10d.) (<i class="calibre12">business habits</i>).  Also bring an ounce of
honeydew from Wilson’s.</p>
<p class="calibre22">R. L. S.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00315"><span class="smcap1">to Mrs. Thomas Stevenson</span></h3>
<p class="calibre22"><i class="calibre12">Brussels</i>, <i class="calibre12">Thursday</i>,
25<i class="calibre12">th July</i> 1872.</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR MOTHER</span>,—I am here
at last, sitting in my room, without coat or waistcoat, and with
both window and door open, and yet perspiring like a terra-cotta
jug or a Gruyère cheese.</p>
<p class="calibre7">We had a very good passage, which we certainly <a id="page31"></a><span id="pgepubid00316" title="p. 31"></span>deserved, in
compensation for having to sleep on cabin floor, and finding
absolutely nothing fit for human food in the whole filthy
embarkation.  We made up for lost time by sleeping on deck a
good part of the forenoon.  When I woke, Simpson was still
sleeping the sleep of the just, on a coil of ropes and (as
appeared afterwards) his own hat; so I got a bottle of Bass and a
pipe and laid hold of an old Frenchman of somewhat filthy aspect
(<i class="calibre12">fiat</i> <i class="calibre12">experimentum in corpore vili</i>) to try my
French upon.  I made very heavy weather of it.  The
Frenchman had a very pretty young wife; but my French always
deserted me entirely when I had to answer her, and so she soon
drew away and left me to her lord, who talked of French politics,
Africa, and domestic economy with great vivacity.  From
Ostend a smoking-hot journey to Brussels.  At Brussels we
went off after dinner to the Parc.  If any person wants to
be happy, I should advise the Parc.  You sit drinking iced
drinks and smoking penny cigars under great old trees.  The
band place, covered walks, etc., are all lit up.  And you
can’t fancy how beautiful was the contrast of the great
masses of lamplit foliage and the dark sapphire night sky with
just one blue star set overhead in the middle of the largest
patch.  In the dark walks, too, there are crowds of people
whose faces you cannot see, and here and there a colossal white
statue at the corner of an alley that gives the place a nice,
<i class="calibre12">artificial</i>, eighteenth century sentiment.  There was
a good deal of summer lightning blinking overhead, and the black
avenues and white statues leapt out every minute into short-lived
distinctness.</p>
<p class="calibre7">I get up to add one thing more.  There is in the hotel a
boy in whom I take the deepest interest.  I cannot tell you
his age, but the very first time I saw him (when I was at dinner
yesterday) I was very much struck with his appearance. 
There is something very leonine in his face, with a dash of the
negro especially, if I remember aright, in the mouth.  He
has a great quantity of dark <a id="page32"></a><span id="pgepubid00317" title="p. 32"></span>hair, curling in great rolls, not in
little corkscrews, and a pair of large, dark, and very steady,
bold, bright eyes.  His manners are those of a prince. 
I felt like an overgrown ploughboy beside him.  He speaks
English perfectly, but with, I think, sufficient foreign accent
to stamp him as a Russian, especially when his manners are taken
into account.  I don’t think I ever saw any one who
looked like a hero before.  After breakfast this morning I
was talking to him in the court, when he mentioned casually that
he had caught a snake in the Riesengebirge.  ‘I have
it here,’ he said; ‘would you like to see
it?’  I said yes; and putting his hand into his
breast-pocket, he drew forth not a dried serpent skin, but the
head and neck of the reptile writhing and shooting out its
horrible tongue in my face.  You may conceive what a fright
I got.  I send off this single sheet just now in order to
let you know I am safe across; but you must not expect letters
often.</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<p class="calibre7"><i class="calibre12">P.S.</i>—The snake was about a yard long, but
harmless, and now, he says, quite tame.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00318"><span class="smcap1">to Mrs. Thomas Stevenson</span></h3>
<p class="calibre22"><i class="calibre12">Hotel Landsberg</i>,
<i class="calibre12">Frankfurt</i>, <i class="calibre12">Monday</i>, 29<i class="calibre12">th</i> <i class="calibre12">July</i>
1872.</p>
<p class="calibre7">. . . <span class="smcap">Last</span> night I met with rather
an amusing adventurette.  Seeing a church door open, I went
in, and was led by most importunate finger-bills up a long stair
to the top of the tower.  The father smoking at the door,
the mother and the three daughters received me as if I was a
friend of the family and had come in for an evening visit. 
The youngest daughter (about thirteen, I suppose, and a pretty
little girl) had been learning English at the school, and was
anxious to play it off upon a real, veritable Englander; so we
had a long talk, and I was shown photographs, etc., Marie and I
talking, and the others looking on with evident delight at having
such a linguist in the family.  <a id="page33"></a><span id="pgepubid00319" title="p. 33"></span>As all my remarks were duly
translated and communicated to the rest, it was quite a good
German lesson.  There was only one contretemps during the
whole interview—the arrival of another visitor, in the
shape (surely) the last of God’s creatures, a wood-worm of
the most unnatural and hideous appearance, with one great striped
horn sticking out of his nose like a boltsprit.  If there
are many wood-worms in Germany, I shall come home.  The most
courageous men in the world must be entomologists.  I had
rather be a lion-tamer.</p>
<p class="calibre7">To-day I got rather a curiosity—<i class="calibre12">Lieder und Balladen
von Robert Burns</i>, translated by one Silbergleit, and not so
ill done either.  Armed with which, I had a swim in the
Main, and then bread and cheese and Bavarian beer in a sort of
café, or at least the German substitute for a café;
but what a falling off after the heavenly forenoons in
Brussels!</p>
<p class="calibre7">I have bought a meerschaum out of local sentiment, and am now
very low and nervous about the bargain, having paid dearer than I
should in England, and got a worse article, if I can form a
judgment.</p>
<p class="calibre7">Do write some more, somebody.  To-morrow I expect I shall
go into lodgings, as this hotel work makes the money disappear
like butter in a furnace.—Meanwhile believe me, ever your
affectionate son,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00320"><span class="smcap1">to Mrs. Thomas Stevenson</span></h3>
<p class="calibre22"><i class="calibre12">Hotel Landsberg</i>,
<i class="calibre12">Thursday</i>, 1<i class="calibre12">st</i> <i class="calibre12">August</i> 1872.</p>
<p class="calibre7">. . . <span class="smcap">Yesterday</span> I walked to
Eckenheim, a village a little way out of Frankfurt, and turned
into the alehouse.  In the room, which was just such as it
would have been in Scotland, were the landlady, two neighbours,
and an old peasant eating raw sausage at the far end.  I
soon got into conversation; and was astonished when the landlady,
having asked whether I were an Englishman, <a id="page34"></a><span id="pgepubid00321" title="p. 34"></span>and received
an answer in the affirmative, proceeded to inquire further
whether I were not also a Scotchman.  It turned out that a
Scotch doctor—a professor—a poet—who wrote
books—<i class="calibre12">gross wie das</i>—had come nearly every day
out of Frankfurt to the <i class="calibre12">Eckenheimer Wirthschaft</i>, and had
left behind him a most savoury memory in the hearts of all its
customers.  One man ran out to find his name for me, and
returned with the news that it was <i class="calibre12">Cobie</i> (Scobie, I
suspect); and during his absence the rest were pouring into my
ears the fame and acquirements of my countryman.  He was, in
some undecipherable manner, connected with the Queen of England
and one of the Princesses.  He had been in Turkey, and had
there married a wife of immense wealth.  They could find
apparently no measure adequate to express the size of his
books.  In one way or another, he had amassed a princely
fortune, and had apparently only one sorrow, his daughter to wit,
who had absconded into a <i class="calibre12">kloster</i>, with a considerable
slice of the mother’s <i class="calibre12">geld</i>.  I told them we had
no klosters in Scotland, with a certain feeling of
superiority.  No more had they, I was
told—‘<i class="calibre12">Hier ist unser Kloster</i>!’ and the
speaker motioned with both arms round the taproom.  Although
the first torrent was exhausted, yet the Doctor came up again in
all sorts of ways, and with or without occasion, throughout the
whole interview; as, for example, when one man, taking his pipe
out of his mouth and shaking his head, remarked
<i class="calibre12">àpropos</i> of nothing and with almost defiant
conviction, ‘<i class="calibre12">Er war ein feiner Mann</i>, <i class="calibre12">der Herr
Doctor</i>,’ and was answered by another with
‘<i class="calibre12">Yaw</i>, <i class="calibre12">yaw</i>, <i class="calibre12">und trank immer rothen
Wein</i>.’</p>
<p class="calibre7">Setting aside the Doctor, who had evidently turned the brains
of the entire village, they were intelligent people.  One
thing in particular struck me, their honesty in admitting that
here they spoke bad German, and advising me to go to Coburg or
Leipsic for German.—‘<i class="calibre12">Sie sprechen da
rein</i>’ (clean), said one; and they all nodded their <a id="page35"></a><span id="pgepubid00322" title="p. 35"></span>heads
together like as many mandarins, and repeated <i class="calibre12">rein</i>, <i class="calibre12">so
rein</i> in chorus.</p>
<p class="calibre7">Of course we got upon Scotland.  The hostess said,
‘<i class="calibre12">Die Schottländer trinken gern Schnapps</i>,’
which may be freely translated, ‘Scotchmen are horrid fond
of whisky.’  It was impossible, of course, to combat
such a truism; and so I proceeded to explain the construction of
toddy, interrupted by a cry of horror when I mentioned the
<i class="calibre12">hot</i> water; and thence, as I find is always the case, to
the most ghastly romancing about Scottish scenery and manners,
the Highland dress, and everything national or local that I could
lay my hands upon.  Now that I have got my German Burns, I
lean a good deal upon him for opening a conversation, and read a
few translations to every yawning audience that I can
gather.  I am grown most insufferably national, you
see.  I fancy it is a punishment for my want of it at
ordinary times.  Now, what do you think, there was a waiter
in this very hotel, but, alas! he is now gone, who sang (from
morning to night, as my informant said with a shrug at the
recollection) what but <i class="calibre12">‘s ist lange her</i>, the German
version of Auld Lang Syne; so you see, madame, the finest lyric
ever written will make its way out of whatsoever corner of patois
it found its birth in.</p>
<p class="poetry">‘<i class="calibre12">Meitz Herz ist im Hochland</i>,
<i class="calibre12">mean Herz ist nicht hier</i>,<br class="calibre5"/>
<i class="calibre12">Mein Herz ist im Hochland im grünen Revier</i>.<br class="calibre5"/>
<i class="calibre12">Im grünen Reviere zu jagen das Reh</i>;<br class="calibre5"/>
<i class="calibre12">Mein Herz ist im Hochland</i>, <i class="calibre12">wo immer ich
geh</i>.’</p>
<p class="calibre7">I don’t think I need translate that for you.</p>
<p class="calibre7">There is one thing that burthens me a good deal in my
patriotic garrulage, and that is the black ignorance in which I
grope about everything, as, for example, when I gave yesterday a
full and, I fancy, a startlingly incorrect account of Scotch
education to a very stolid German on a garden bench: he sat and
perspired under it, however with much composure.  I am
generally glad enough to <a id="page36"></a><span id="pgepubid00323" title="p. 36"></span>fall back again, after these
political interludes, upon Burns, toddy, and the Highlands.</p>
<p class="calibre7">I go every night to the theatre, except when there is no
opera.  I cannot stand a play yet; but I am already very
much improved, and can understand a good deal of what goes
on.</p>
<p class="calibre7"><i class="calibre12">Friday</i>, <i class="calibre12">August</i> 2, 1872.—In the evening, at
the theatre, I had a great laugh.  Lord Allcash in <i class="calibre12">Fra
Diavolo</i>, with his white hat, red guide-books, and bad German,
was the <i class="calibre12">pièce-de-résistance</i> from a humorous
point of view; and I had the satisfaction of knowing that in my
own small way I could minister the same amusement whenever I
chose to open my mouth.</p>
<p class="calibre7">I am just going off to do some German with Simpson.—Your
affectionate son,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00324"><span class="smcap1">to Thomas Stevenson</span></h3>
<p class="calibre22"><i class="calibre12">Frankfurt</i>, <i class="calibre12">Rosengasse</i>
13, <i class="calibre12">August</i> 4, 1872.</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR FATHER</span>,—You will
perceive by the head of this page that we have at last got into
lodgings, and powerfully mean ones too.  If I were to call
the street anything but <i class="calibre12">shady</i>, I should be boasting. 
The people sit at their doors in shirt-sleeves, smoking as they
do in Seven Dials of a Sunday.</p>
<p class="calibre7">Last night we went to bed about ten, for the first time
<i class="calibre12">householders</i> in Germany—real Teutons, with no
deception, spring, or false bottom.  About half-past one
there began such a trumpeting, shouting, pealing of bells, and
scurrying hither and thither of feet as woke every person in
Frankfurt out of their first sleep with a vague sort of
apprehension that the last day was at hand.  The whole
street was alive, and we could hear people talking in their
rooms, or crying to passers-by from their windows, all around
us.  At last I made out what a man was <a id="page37"></a><span id="pgepubid00325" title="p. 37"></span>saying in the
next room.  It was a fire in Sachsenhausen, he said
(Sachsenhausen is the suburb on the other side of the Main), and
he wound up with one of the most tremendous falsehoods on record,
‘<i class="calibre12">Hier alles ruht</i>—here all is
still.’  If it can be said to be still in an engine
factory, or in the stomach of a volcano when it is meditating an
eruption, he might have been justified in what he said, but not
otherwise.  The tumult continued unabated for near an hour;
but as one grew used to it, it gradually resolved itself into
three bells, answering each other at short intervals across the
town, a man shouting, at ever shorter intervals and with
superhuman energy, ‘<i class="calibre12">Feuer</i>,—<i class="calibre12">im
Sachsenhausen</i>, and the almost continuous winding of all
manner of bugles and trumpets, sometimes in stirring flourishes,
and sometimes in mere tuneless wails.  Occasionally there
was another rush of feet past the window, and once there was a
mighty drumming, down between us and the river, as though the
soldiery were turning out to keep the peace.  This was all
we had of the fire, except a great cloud, all flushed red with
the glare, above the roofs on the other side of the Gasse; but it
was quite enough to put me entirely off my sleep and make me
keenly alive to three or four gentlemen who were strolling
leisurely about my person, and every here and there leaving me
somewhat as a keepsake. . . . However, everything has its
compensation, and when day came at last, and the sparrows awoke
with trills and <i class="calibre12">carol-ets</i>, the dawn seemed to fall on me
like a sleeping draught.  I went to the window and saw the
sparrows about the eaves, and a great troop of doves go strolling
up the paven Gasse, seeking what they may devour.  And so to
sleep, despite fleas and fire-alarms and clocks chiming the hours
out of neighbouring houses at all sorts of odd times and with the
most charming want of unanimity.</p>
<p class="calibre7">We have got settled down in Frankfurt, and like the place very
much.  Simpson and I seem to get on very well
together.  We suit each other capitally; and it is an <a id="page38"></a><span id="pgepubid00326" title="p. 38"></span>awful joke to
be living (two would-be advocates, and one a baronet) in this
supremely mean abode.</p>
<p class="calibre7">The abode is, however, a great improvement on the hotel, and I
think we shall grow quite fond of it.—Ever your
affectionate son,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00327"><span class="smcap1">to Mrs. Thomas Stevenson</span></h3>
<p class="calibre22">13 <i class="calibre12">Rosengasse</i>,
<i class="calibre12">Frankfurt</i>, <i class="calibre12">Tuesday Morning</i>, <i class="calibre12">August</i> 1872.</p>
<p class="calibre7">. . . Last night I was at the theatre and heard <i class="calibre12">Die
Judin</i> (<i class="calibre12">La Juive</i>), and was thereby terribly
excited.  At last, in the middle of the fifth act, which was
perfectly beastly, I had to slope.  I could stand even
seeing the cauldron with the sham fire beneath, and the two
hateful executioners in red; but when at last the girl’s
courage breaks down, and, grasping her father’s arm, she
cries out—O so shudderfully!—I thought it high time
to be out of that <i class="calibre12">galère</i>, and so I do not know yet
whether it ends well or ill; but if I ever afterwards find that
they do carry things to the extremity, I shall think more meanly
of my species.  It was raining and cold outside, so I went
into a <i class="calibre12">Bierhalle</i>, and sat and brooded over a
<i class="calibre12">Schnitt</i> (half-glass) for nearly an hour.  An opera is
far more <i class="calibre12">real</i> than real life to me.  It seems as if
stage illusion, and particularly this hardest to swallow and most
conventional illusion of them all—an opera—would
never stale upon me.  I wish that life was an opera.  I
should like to <i class="calibre12">live</i> in one; but I don’t know in what
quarter of the globe I shall find a society so constituted. 
Besides, it would soon pall: imagine asking for three-kreuzer
cigars in recitative, or giving the washerwoman the inventory of
your dirty clothes in a sustained and <i class="calibre12">flourishous</i>
aria.</p>
<p class="calibre7">I am in a right good mood this morning to sit here and write
to you; but not to give you news.  There is a great stir of
life, in a quiet, almost country fashion, all about us
here.  Some one is hammering a beef-steak in <a id="page39"></a><span id="pgepubid00328" title="p. 39"></span>the
<i class="calibre12">rez-de-chaussée</i>: there is a great clink of pitchers
and noise of the pump-handle at the public well in the little
square-kin round the corner.  The children, all seemingly
within a month, and certainly none above five, that always go
halting and stumbling up and down the roadway, are ordinarily
very quiet, and sit sedately puddling in the gutter, trying, I
suppose, poor little devils! to understand their
<i class="calibre12">Muttersprache</i>; but they, too, make themselves heard from
time to time in little incomprehensible antiphonies, about the
drift that comes down to them by their rivers from the strange
lands higher up the Gasse.  Above all, there is here such a
twittering of canaries (I can see twelve out of our window), and
such continual visitation of grey doves and big-nosed sparrows,
as make our little bye-street into a perfect aviary.</p>
<p class="calibre7">I look across the Gasse at our opposite neighbour, as he
dandles his baby about, and occasionally takes a spoonful or two
of some pale slimy nastiness that looks like <i class="calibre12">dead
porridge</i>, if you can take the conception.  These two are
his only occupations.  All day long you can hear him singing
over the brat when he is not eating; or see him eating when he is
not keeping baby.  Besides which, there comes into his house
a continual round of visitors that puts me in mind of the
luncheon hour at home.  As he has thus no ostensible
avocation, we have named him ‘the W.S.’ to give a
flavour of respectability to the street.</p>
<p class="calibre7">Enough of the Gasse.  The weather is here much
colder.  It rained a good deal yesterday; and though it is
fair and sunshiny again to-day, and we can still sit, of course,
with our windows open, yet there is no more excuse for the
siesta; and the bathe in the river, except for cleanliness, is no
longer a necessity of life.  The Main is very swift. 
In one part of the baths it is next door to impossible to swim
against it, and I suspect that, out in the open, it would be
quite impossible.—Adieu, my dear mother, and believe me,
ever your affectionate son,</p>
<p class="calibre22"><span class="smcap">Robert Louis
Stevenson</span><br class="calibre5"/>
(<i class="calibre12">Rentier</i>).</p>
<h3 class="calibre23 sigil_not_in_toc" id="pgepubid00329"><a class="calibre1" id="page40"></a><span class="calibre1" id="pgepubid00330" title="p. 40"></span><span class="smcap1">to Charles Baxter</span></h3>
<p class="calibre22">17 <i class="calibre12">Heriot Row</i>,
<i class="calibre12">Edinburgh</i>, <i class="calibre12">Sunday</i>, <i class="calibre12">February</i> 2, 1873.</p>
<p class="calibre7"><span class="gutsmall1">MY DEAR BAXTER</span>,—The
thunderbolt has fallen with a vengeance now.  On Friday
night after leaving you, in the course of conversation, my father
put me one or two questions as to beliefs, which I candidly
answered.  I really hate all lying so much now—a new
found honesty that has somehow come out of my late
illness—that I could not so much as hesitate at the time;
but if I had foreseen the real hell of everything since, I think
I should have lied, as I have done so often before.  I so
far thought of my father, but I had forgotten my mother. 
And now! they are both ill, both silent, both as down in the
mouth as if—I can find no simile.  You may fancy how
happy it is for me.  If it were not too late, I think I
could almost find it in my heart to retract, but it is too late;
and again, am I to live my whole life as one falsehood?  Of
course, it is rougher than hell upon my father, but can I help
it?  They don’t see either that my game is not the
light-hearted scoffer; that I am not (as they call me) a careless
infidel.  I believe as much as they do, only generally in
the inverse ratio: I am, I think, as honest as they can be in
what I hold.  I have not come hastily to my views.  I
reserve (as I told them) many points until I acquire fuller
information, and do not think I am thus justly to be called
‘horrible atheist.’</p>
<p class="calibre7">Now, what is to take place?  What a curse I am to my
parents!  O Lord, what a pleasant thing it is to have just
<i class="calibre12">damned</i> the happiness of (probably) the only two people who
care a damn about you in the world.</p>
<p class="calibre7">What is my life to be at this rate?  What, you
rascal?  <a id="page41"></a><span id="pgepubid00331" title="p. 41"></span>Answer—I have a pistol at your throat.  If
all that I hold true and most desire to spread is to be such
death, and a worse than death, in the eyes of my father and
mother, what the <i class="calibre12">devil</i> am I to do?</p>
<p class="calibre7">Here is a good heavy cross with a vengeance, and all rough
with rusty nails that tear your fingers, only it is not I that
have to carry it alone; I hold the light end, but the heavy
burden falls on these two.</p>
<p class="calibre7">Don’t—I don’t know what I was going to
say.  I am an abject idiot, which, all things considered, is
not remarkable.—Ever your affectionate and horrible
atheist,</p>
<p class="calibre22">R. L. <span class="smcap">Stevenson</span>.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0004.vue?c024a51c